import type { ComponentType } from 'react';

type ComponentModule = { default: ComponentType<any> };
type Preloader = () => Promise<ComponentModule>;

/*
 * This function allows vite to access compiled chunks on runtime. The keys are
 * used as access name and the import still gets triggered. This is helpful if
 * the path that needs to be loaded is dynamic. For example when using the path
 * as a prop to be passed on to the react-router-link for onMouseEnter.
 *
 * To ensure compatibility it's used in router-object as well.
 *
 * Pseudo JSON notation for better readability.
 * */
const preloadChunks = {
  // -- Auth --
  'auth.verifyEmail': () => import('@/pages/Auth/Register/VerifyEmail'),
  'auth.accountType': () => import('@/pages/Auth/Register/AccountType'),
  'auth.verifyPhone': () => import('@/pages/Auth/Register/VerifyPhone'),
  'auth.kyc': () => import('@/pages/Auth/Kyc'),
  'auth.setPin': () => import('@/pages/Auth/SetPin'),

  // -- Contacts --
  'contacts.main': () => import('@/pages/Contacts'),

  // -- Account --
  'account.invoicing': () => import('@/pages/Invoicing'),
  'account.members': () => import('@/pages/Members'),
  'account.subscription': () => import('@/pages/Subscription'),
  'profile.main': () => import('@/pages/Profile'),
  'profile.phoneVerification': () =>
    import('@/pages/Profile/PhoneVerification'),

  // -- OAuth --
  'oauth.authorize': () => import('@/pages/OAuthAuthorize'),

  // -- Review --
  'review.document': () => import('@/pages/Review/Document'),

  // -- SafeTransfer --
  'safeTransfer.main': () => import('@/pages/SafeTransfer'),
  'safeTransfer.form': () => import('@/pages/SafeTransfer/Form'),
  'safeTransfer.access': () => import('@/pages/SafeTransferAccess'),

  // -- Settings --
  'settings.main': () => import('@/pages/Settings'),
  'settings.apiKeys.addNew': () =>
    import('@/pages/Settings/AddNewAPIKey/AddNewAPIKey'),

  // -- Sign --
  'sign.document': () => import('@/pages/Sign/Document'),

  // -- Signings --
  'signings.detail': () => import('@/pages/Signings/Detail'),
  'signings.report.auditTrail': () =>
    import('@/pages/Signings/Detail/AuditTrail'),
  'signings.report.summary': () => import('@/pages/Signings/Detail/Summary'),
  'signings.workflow': () =>
    import('@/pages/Signings/Workflow/WorkflowAsSignings'),

  // -- Subscription --
  'subscription.checkout': () => import('@/pages/Subscription/Checkout'),

  // -- Templates --
  'templates.main': () => import('@/pages/Templates'),
  'templates.workflow': () =>
    import('@/pages/Templates/Workflow/WorkflowAsTemplates'),

  // -- Tools --
  'tools.docx': () => import('@/pages/Tools/DocxConversion'),
  'tools.main': () => import('@/pages/Tools'),
  'tools.merge': () => import('@/pages/Tools/PdfMerge'),
  'tools.pdfa': () => import('@/pages/Tools/PdfaConversion'),
  'tools.redact': () => import('@/pages/Tools/Redaction'),

  // -- Content Credentials (C2PA POC) --
  'contentCredentials.main': () => import('@/pages/ContentCredentials'),
} as const satisfies Record<string, Preloader>;

export default preloadChunks;

/*
 * A list of all available named vite chunks to lazy load. See preloadChunks.
 * */
export type PreloadChunksKey = keyof typeof preloadChunks;
